<script setup lang="ts">
/**
 * The one route whose path does not follow its feature folder: the feature is `auth` — the job —
 * and the door is `/login`. Declared here rather than in the router so the file that answers a URL
 * is the file that names it. `src/app/router.ts` treats `/login` as the only public path.
 *
 * `definePage` is a compiler macro, so it belongs inside `<script setup>` alongside `defineProps` —
 * in a separate `<script>` block it is silently not applied and the route keeps its file-derived
 * path. `router.test.ts` resolves `/login` through the real router for exactly that reason.
 */
definePage({ path: "/login" });

import { Scale, ShieldCheck } from "lucide-vue-next";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Message from "primevue/message";
import Password from "primevue/password";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { WORKSPACE_PATH } from "@/app/router";
import { HOME } from "@/shared";
import type { LoginFormError } from "../login-error";
import { classifyLoginError } from "../login-error";
import { useSession } from "../session";
import { useAuth } from "../useAuth";

/**
 * Username, password, an optional Casino partition selector.
 *
 * `casinoId` is deliberately labelled as a **partition**, never as "Casino you manage" — it picks
 * which partition the credentials are checked against, not an authorization grant. Omitting it
 * selects the platform_admin partition (S2). Authorization itself is decided by the API on every
 * later request; this field only routes the login attempt.
 */
const { t } = useI18n();
const session = useSession();
const { login, isLoggingIn } = useAuth();
const route = useRoute();
const router = useRouter();

const username = ref("");
const password = ref("");
const casinoId = ref("");

const formError = ref<LoginFormError | null>(null);

const workspaceLabel = computed(() => (session.role ? t(`workspace.${HOME[session.role]}`) : null));

/**
 * `route.query.reason === "session-expired"` is set by exactly one source: `app/session-guard.ts`,
 * when a request answered `STAFF_TOKEN_INVALID` mid-shift and centrally ended the session
 * (`docs/UNOBSERVED.md` §1.5). Shown instead of a bare sign-in form so an operator bounced here
 * mid-shift is told the truth — the session ended, not "did I mistype my password" — the plain
 * `router.beforeEach` bounce for someone who was never signed in carries no `reason` and shows
 * nothing here.
 */
const sessionExpired = computed(() => route.query.reason === "session-expired");

/** Set by `app/idle-guard.ts` and by the router guard after a reload past the idle window. */
const idleLocked = computed(() => route.query.reason === "idle");

/**
 * Honours a deep link the guard bounced through `/login`'s own `redirect` query param
 * (`app/router.ts`) — the same "don't lose the operator's place" property `9480006` gave a reload,
 * extended to a login that followed a bounce, and the one `app/session-guard.ts` relies on for a
 * session that dies mid-shift. Falls back to `HOME[role]` when there is none, or when the stored
 * value is not a same-origin path this app could resolve — an external or protocol-relative URL
 * slipped into the query string must never be followed.
 *
 * `router.beforeEach` still runs on the navigation this feeds — if the destination is a workspace
 * this role cannot reach, `canEnter`'s own redirect to `HOME[role]` fires exactly as it would for a
 * typed URL, so this function does not need to re-check reachability itself.
 */
function redirectTarget(): string {
  const home = session.role ? WORKSPACE_PATH[HOME[session.role]] : "/";
  const raw = route.query.redirect;
  const candidate = typeof raw === "string" ? raw : null;
  if (
    !candidate ||
    candidate === "/login" ||
    !candidate.startsWith("/") ||
    candidate.startsWith("//")
  ) {
    return home;
  }
  return candidate;
}

async function onSubmit(): Promise<void> {
  formError.value = null;
  try {
    await login({
      username: username.value,
      password: password.value,
      ...(casinoId.value.trim() ? { casinoId: casinoId.value.trim() } : {}),
    });
    // The whole reason this screen exists is to land the operator on a workspace — see
    // `docs/UNOBSERVED.md` §1.1. `replace`, not `push`: `/login` should not be one `back` tap away
    // once signed in, the same rule `AppShell.vue`'s `leaveBreakGlass` follows.
    await router.replace(redirectTarget());
  } catch (err) {
    formError.value = classifyLoginError(err);
  }
}
</script>

<template>
  <main class="login">
    <!-- The thesis: the money room's one promise, stated in brass before the door. The hero of the
         page, per the design — a headline, not a form. -->
    <section class="hero" aria-hidden="true">
      <p class="eyebrow">KIOSK CONSOLE · CASH OPERATIONS</p>
      <h1 class="thesis">
        {{ t("auth.thesisHeadlineLead") }}<br /><em>{{ t("auth.thesisHeadlineEm") }}</em>
      </h1>
      <p class="thesis-body">{{ t("auth.thesisBody") }}</p>
      <ul class="points">
        <li>{{ t("auth.thesisPoint1") }}</li>
        <li>{{ t("auth.thesisPoint2") }}</li>
        <li>{{ t("auth.thesisPoint3") }}</li>
      </ul>
    </section>

    <!-- The sign-in card, framed by registration marks — the door to a money system. -->
    <section class="panel c-frame">
      <div class="brand">
        <span class="brandmark" aria-hidden="true"><Scale :size="20" /></span>
        <span class="brandtext">
          <span class="brand-name">{{ t("shell.title") }}</span>
          <span class="brand-tagline">{{ t("auth.brandTagline") }}</span>
        </span>
      </div>

      <h2 class="signin-title">{{ t("auth.title") }}</h2>

      <Message
        v-if="idleLocked && !session.isAuthed"
        severity="warn"
        :closable="false"
        data-testid="idle-locked-message"
      >
        {{ t("auth.idleLocked") }}
      </Message>
      <Message
        v-if="sessionExpired && !session.isAuthed"
        severity="warn"
        :closable="false"
        data-testid="session-expired-message"
      >
        {{ t("auth.sessionExpired") }}
      </Message>

      <form v-if="!session.isAuthed" class="form" @submit.prevent="onSubmit">
        <label class="field" data-field="username">
          <span>{{ t("auth.username") }}</span>
          <InputText v-model="username" autocomplete="username" required />
        </label>

        <label class="field" data-field="password">
          <span>{{ t("auth.password") }}</span>
          <Password
            v-model="password"
            autocomplete="current-password"
            :feedback="false"
            toggle-mask
            required
            fluid
          />
        </label>

        <label class="field" data-field="casino-id">
          <span>{{ t("auth.casinoId") }}</span>
          <InputText v-model="casinoId" autocomplete="off" />
          <small class="hint">{{ t("auth.casinoIdHint") }}</small>
        </label>

        <!-- 429 is its own branch, not a generic error: a misconfigured reverse proxy shares one
             rate-limit counter across every workstation, and staff otherwise conclude their own
             account or password is broken. -->
        <Message v-if="formError?.kind === 'rate-limited'" severity="warn" :closable="false">
          {{ t("error.rateLimited", { minutes: formError.minutes }) }}
        </Message>
        <Message
          v-else-if="formError?.kind === 'invalid-credentials'"
          severity="error"
          :closable="false"
        >
          {{ t("auth.invalidCredentials") }}
        </Message>
        <Message v-else-if="formError?.kind === 'generic'" severity="error" :closable="false">
          {{ t("auth.genericError") }}
        </Message>

        <Button
          type="submit"
          data-testid="login-submit"
          :label="isLoggingIn ? t('auth.submitting') : t('auth.submit')"
          :disabled="isLoggingIn"
        />
      </form>

      <!-- The whoami result, not the login response's `staff` snapshot, is what the store holds —
           see useAuth.ts. `workspaceLabel` reads that adopted role back out via HOME, so what is
           shown here is already the authority's answer, not a guess made from the form. -->
      <p v-else class="signed-in">
        {{ t("auth.signedIn", { username: session.staff?.username, workspace: workspaceLabel }) }}
      </p>

      <p class="audited">
        <ShieldCheck :size="14" aria-hidden="true" />
        <span>{{ t("auth.audited") }}</span>
      </p>
    </section>
  </main>
</template>

<style scoped>
/* Two columns: the brass thesis, then the framed sign-in card. Centered in the login shell. */
.login {
  flex: 1;
  align-self: center;
  width: 100%;
  max-width: 1080px;
  margin: auto;
  display: grid;
  grid-template-columns: 1.05fr minmax(340px, 420px);
  gap: var(--s7);
  align-items: center;
  padding: var(--s5) var(--s4) 8vh;
}

/* The thesis — the hero. Brass headline, then the promises the room actually keeps. */
.hero {
  max-width: 480px;
}
.eyebrow {
  display: flex;
  align-items: center;
  gap: var(--s3);
  margin: 0 0 var(--s4);
  font: var(--t-micro);
  font-family: var(--mono);
  letter-spacing: 0.16em;
  color: var(--accent);
}
.eyebrow::after {
  content: "";
  height: 1px;
  flex: 0 0 34px;
  background: linear-gradient(90deg, var(--accent), transparent);
}
/* The thesis is an ink headline with ONE brass word — brass everywhere is brass nowhere. */
.thesis {
  margin: 0 0 var(--s4);
  font-family: var(--sans);
  font-weight: 700;
  font-size: clamp(38px, 5vw, 58px);
  line-height: 1.06;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.thesis em {
  font-style: normal;
  color: var(--accent-hover);
}
.thesis-body {
  margin: 0 0 var(--s5);
  max-width: 40ch;
  font: var(--t-body);
  color: var(--ink-2);
}
.points {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--s3);
}
.points li {
  position: relative;
  padding-inline-start: var(--s5);
  font: var(--t-small);
  color: var(--ink-2);
}
/* An assay-checklist tick: the mark struck inside a small brass box, not a bare glyph. */
.points li::before {
  content: "✓";
  position: absolute;
  inset-inline-start: 0;
  top: 1px;
  width: 16px;
  height: 16px;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 700;
  color: var(--accent);
  border: 1.5px solid var(--accent);
  border-radius: 3px;
}

/* The card, bracketed by registration marks (`.c-frame`), lifted off the ground — the one surface
   that floats, so it carries a shadow the flat tiles never do, and a faint top-to-bottom fall. */
.panel {
  display: flex;
  flex-direction: column;
  gap: var(--s4);
  padding: var(--s6) var(--s6) var(--s5);
  background: linear-gradient(180deg, var(--surface), var(--ground));
  border: 1px solid var(--rule-2);
  border-radius: var(--r-lg);
  box-shadow: var(--e2-shadow);
}
.brand {
  display: flex;
  align-items: center;
  gap: var(--s3);
}
.brandmark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--r-sm);
  background: var(--accent-subtle);
  color: var(--accent);
  border: 1px solid rgb(201 165 87 / 0.3);
}
.brandtext {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.brand-name {
  font: var(--t-title);
  font-weight: 600;
}
.brand-tagline {
  font: var(--t-micro);
  font-family: var(--mono);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-3);
}
.signin-title {
  margin: var(--s2) 0 0;
  font: var(--t-section);
  color: var(--ink);
}
.form {
  display: flex;
  flex-direction: column;
  gap: var(--s4);
}
.field {
  display: flex;
  flex-direction: column;
  gap: var(--s1);
  font: var(--t-small);
  font-weight: 550;
  color: var(--ink-2);
}
.hint {
  color: var(--ink-3);
  font: var(--t-micro);
  font-weight: 430;
}
/* Identifiers type in the mono face — the rail's username chip already obeys this. */
.field[data-field="username"] :deep(input),
.field[data-field="casino-id"] :deep(input) {
  font-family: var(--mono);
}
.form :deep(.c-btn) {
  width: 100%;
  margin-top: var(--s2);
}
.signed-in {
  margin: 0;
}
.audited {
  display: flex;
  align-items: center;
  gap: var(--s2);
  margin: var(--s2) 0 0;
  padding-top: var(--s3);
  border-top: 1px solid var(--rule);
  font: var(--t-micro);
  color: var(--ink-3);
}
.audited svg {
  flex-shrink: 0;
  color: var(--accent);
}

/* Narrow terminals: the thesis yields to the form — the job on a phone is signing in. */
@media (max-width: 900px) {
  .login {
    grid-template-columns: 1fr;
    max-width: 440px;
    gap: var(--s5);
  }
  .hero {
    display: none;
  }
}
</style>
